import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, useTheme } from '@mui/material';
import { IconAlertTriangle, IconChevronRight } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { AuditEvent, isErrorAuditEvent } from './auditData';
import { getKindLabel } from './auditLabels';
import { CopyableId } from './CopyableId';
import { KnowledgeCoachingLinks } from './KnowledgeCoachingLinks';
import { PayloadDetails } from './PayloadDetails';
import { SourceLinks } from './SourceLinks';
import { WorkflowVersionLink } from './WorkflowVersionLink';
import { RecommendedFix } from 'src/pages/events/error/recommended-fix/RecommendedFix';

const KindChip = ({ event }: { event: AuditEvent }) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const isError = isErrorAuditEvent(event);
  const label = getKindLabel(t, event.kind);

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: isError ? palette.colors.red[200] : palette.colors.slate[100],
        borderRadius: '4px',
        display: 'flex',
        flexShrink: 0,
        gap: '4px',
        px: '6px',
        py: '2px',
      }}
    >
      {isError && (
        <IconAlertTriangle color={palette.colors.red[500]} size={12} />
      )}
      <Typography
        color={isError ? palette.colors.red[700] : palette.colors.grey[700]}
        variant='font11'
      >
        {isError && event.severity
          ? t('conversationAudit.kindWithSeverity', {
              label,
              severity: event.severity,
            })
          : label}
      </Typography>
    </Box>
  );
};

// The error's own identifier, the Log page's How-to-fix guidance for it, and the
// "View failed step" link that guidance already carries — all rendered from the
// recorded event, so the drawer and the Log page cannot disagree.
const ErrorFixDetails = ({
  errorOrdinal,
  errorTotal,
  event,
  onJumpToError,
}: {
  event: AuditEvent;
  errorOrdinal: number;
  errorTotal: number;
  onJumpToError: (targetOrdinal: number) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const error = event.error;

  if (!error) {
    return null;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '10px', pt: '2px' }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: '12px' }}>
        <Box
          sx={{
            alignItems: 'center',
            columnGap: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            rowGap: '4px',
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}>
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {t('conversationAudit.errorIdLabel')}
            </Typography>
            <CopyableId
              ariaLabel={t('conversationAudit.copyErrorId', {
                errorId: error.error_id,
              })}
              value={error.error_id}
            />
          </Box>
          {error.trace_id && (
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}>
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {t('conversationAudit.traceIdLabel')}
              </Typography>
              <CopyableId
                ariaLabel={t('conversationAudit.copyTraceId')}
                value={error.trace_id}
              />
            </Box>
          )}
        </Box>
        {errorTotal > 1 && (
          <Box
            component='button'
            onClick={() => onJumpToError((errorOrdinal + 1) % errorTotal)}
            sx={{
              alignItems: 'center',
              bgcolor: palette.colors.white,
              border: `1px solid ${palette.colors.red[500]}`,
              borderRadius: '16px',
              cursor: 'pointer',
              display: 'flex',
              flexShrink: 0,
              font: 'inherit',
              gap: '2px',
              ml: 'auto',
              px: '8px',
              py: '3px',
            }}
          >
            <Typography color={palette.colors.red[700]} variant='font11'>
              {t('conversationAudit.errorPosition', {
                current: errorOrdinal + 1,
                total: errorTotal,
              })}
            </Typography>
            <IconChevronRight color={palette.colors.red[700]} size={12} />
          </Box>
        )}
      </Box>
      <RecommendedFix error={error} mode='compact' />
    </Box>
  );
};

// A dot-and-rail timeline: each event gets its kind chip, wall-clock time and
// its own latency, the plain-language line, then the safe secondary detail —
// with payloads, sources and remediation one further click in.
export const EvidenceTimeline = ({
  errorOrdinalByEvent,
  errorTotal,
  events,
  onJumpToError,
}: {
  events: AuditEvent[];
  errorOrdinalByEvent: WeakMap<AuditEvent, number>;
  errorTotal: number;
  onJumpToError: (targetOrdinal: number) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const togglePayload = (index: number) =>
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: '8px' }}>
      {events.map((event, index) => {
        const isError = isErrorAuditEvent(event);
        const isLast = index === events.length - 1;
        const isExpanded = expanded.has(index);
        const hasPayload = Boolean(event.payload);
        const errorIndex = errorOrdinalByEvent.get(event);

        return (
          <Box
            data-audit-error={isError || undefined}
            data-audit-error-index={errorIndex}
            key={`${event.kind}-${event.timestamp}-${index}`}
            sx={{ display: 'flex', gap: '8px' }}
          >
            {/* Avatar-width, so the dots share the avatar column. */}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '24px',
              }}
            >
              <Box
                sx={{
                  bgcolor: isError
                    ? palette.colors.red[500]
                    : palette.colors.grey[500],
                  borderRadius: '50%',
                  flexShrink: 0,
                  height: '6px',
                  mt: '8px',
                  width: '6px',
                }}
              />
              {!isLast && (
                <Box
                  sx={{
                    bgcolor: palette.colors.slate[200],
                    flex: 1,
                    width: '1px',
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: '4px',
                minWidth: 0,
                pb: '12px',
              }}
            >
              <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                <KindChip event={event} />
              </Box>
              <Box>
                {isError && event.category && (
                  <Typography
                    color={palette.colors.red[700]}
                    variant='font12Medium'
                  >
                    {event.detail
                      ? t('conversationAudit.errorCategoryPrefix', {
                          category: event.category,
                        })
                      : event.category}
                  </Typography>
                )}
                {event.workflowLink ? (
                  <WorkflowVersionLink event={event} />
                ) : (
                  event.detail && (
                    <Typography variant='font12'>{event.detail}</Typography>
                  )
                )}
              </Box>

              {event.aiInstructionIds && event.aiInstructionIds.length > 0 && (
                <KnowledgeCoachingLinks
                  instructionIds={event.aiInstructionIds}
                />
              )}

              {event.sources && <SourceLinks sources={event.sources} />}

              {hasPayload ? (
                <Box
                  aria-expanded={isExpanded}
                  component='button'
                  onClick={() => togglePayload(index)}
                  sx={{
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    background: 'none',
                    border: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    font: 'inherit',
                    gap: '2px',
                    p: 0,
                    textAlign: 'left',
                  }}
                >
                  <IconChevronRight
                    color={palette.colors.grey[600]}
                    size={13}
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 140ms ease',
                    }}
                  />
                  <Box sx={{ textDecoration: 'underline' }}>
                    <Typography
                      color={palette.colors.grey[700]}
                      variant='font12'
                    >
                      {event.evidence ?? t('conversationAudit.showPayload')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                event.evidence && (
                  <Typography color={palette.colors.grey[600]} variant='font11'>
                    {event.evidence}
                  </Typography>
                )
              )}

              {hasPayload && (
                <Collapse in={isExpanded} timeout={200}>
                  <PayloadDetails payload={event.payload ?? {}} />
                </Collapse>
              )}

              {typeof errorIndex === 'number' && (
                <ErrorFixDetails
                  errorOrdinal={errorIndex}
                  errorTotal={errorTotal}
                  event={event}
                  onJumpToError={onJumpToError}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
